import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Form, FormText, PasswordField } from '../../../components/form';
import { Button } from '../../../components/general';
import { ErrorModal } from '../../../components/modals';

import {
  URL,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
} from '../../../constants';

import {
  getUserData,
  verifyResetPwCode,
  resetPassword,
} from '../../../services/api/auth';

import { isEmpty } from '../../../utils';

import OnboardingPage from '../components/onboardingPage';
import SendResentPwEmailComponent from '../components/sendResetPwEmailComponent';
import { validateResetPasswordForm } from '../components/validations';

const ResetPassword = () => {
  const queryParameters = new URLSearchParams(window.location.search);
  const id = queryParameters.get('id') ?? '';
  const code = queryParameters.get('code') ?? '';

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [newReqEmail, setNewReqEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrorModal, setShowErrorModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const verifyAndProceed = async () => {
      try {
        setIsLoading(true);

        const userData = await getUserData(id);

        sessionStorage.setItem('email', userData?.email);
        setNewReqEmail(userData?.email ?? '');

        const isVerified = await verifyResetPwCode(id, code);
        if (!isVerified) setShowErrorModal(true);
      } catch (err) {
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (!id) navigate('/');
    else verifyAndProceed();
  }, [code, id, navigate]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({});
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let errors = await validateResetPasswordForm(formData);

    if (!isEmpty(errors)) {
      setErrors(errors);
      return;
    }

    try {
      setIsResetting(true);

      const isSuccess = await resetPassword(id, code, formData.password);

      if (isSuccess) {
        toast.success('Password changed successfully!');
        navigate(URL.LOGIN);
      }

      setIsResetting(false);
    } catch {
      toast.error('Something went wrong. Try again later');
    }
  };

  return (
    <OnboardingPage
      title="Reset Password"
      heading="Reset your password"
      isLoading={isLoading}
    >
      <Form onSubmit={onSubmit} disabled={isResetting}>
        <div>
          <PasswordField
            label="New password"
            name="password"
            value={formData.password}
            onChange={onChange}
            isInvalid={!!errors.password}
            errMsg={errors.password}
          />

          <FormText
            text={`Password must be ${PASSWORD_MIN_LENGTH}-${PASSWORD_MAX_LENGTH} characters long, contain at least 1 lowercase character, 1 uppercase character, 1 number, and 1 special character.`}
            color="white"
          />
        </div>

        <PasswordField
          label="Confirm new password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={onChange}
          isInvalid={!!errors.confirmPassword}
          errMsg={errors.confirmPassword}
        />

        <Button className="w-full" isSubmitBtn isProcessing={isResetting}>
          Reset Password
        </Button>
      </Form>

      <ErrorModal
        show={showErrorModal}
        heading="Sorry, the link is invalid or expired"
        subheading="You may request another email to receive a new link"
        btn={
          <SendResentPwEmailComponent
            email={newReqEmail ?? ''}
            mainBtnText="Request New Link"
            hasReturnHomeBtn
          />
        }
        isContactUs
      />
    </OnboardingPage>
  );
};

export default ResetPassword;
